import { type FC } from "react";
import { Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { type Organism } from "@/graphql.ts";
import { SelMessage } from "@/messages/SelMessage.tsx";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { useFetchGenomeAnnotationStatistics } from "@/ui-modules/entities/genomes/graphql/useFetchGenomeStatistics.ts";

export interface IGenomicAnnotationCardProps extends GridCard {
  genomeId: Organism["id"];
  sequenced: boolean;
}

const GenomicAnnotationCard: FC<IGenomicAnnotationCardProps> = (props) => {
  const { data, loading, error } = useFetchGenomeAnnotationStatistics({ genomeId: props.genomeId });
  const genomeStatistics = data;

  return (
    <Card key="genome-info-card" title={"Genomic Annotations"}>
      {loading && !data ? (
        <LoadingSpinner />
      ) : genomeStatistics == undefined ? (
        // Fallback. Usually graphql errors are handled globally
        <ContentPlaceholder>{error ? error.message : "No data"}</ContentPlaceholder>
      ) : props.sequenced ? (
        <>
          <CardRowSimple label={"Size"} value={formatNumberByThousands(genomeStatistics.genomeSize) + " bp"} />
          <CardRowSimple label={"Contigs"} value={formatNumberByThousands(genomeStatistics.contigCount)} />
          <CardRowSimple label={"Number of ORFs"} value={formatNumberByThousands(genomeStatistics.orfsCount)} />
          <CardRowSimple label={"Annotated genes"} value={formatNumberByThousands(genomeStatistics.geneCount)} />
          <CardRowSimple
            label={"Annotated transcripts"}
            value={formatNumberByThousands(genomeStatistics.transcriptsCount)}
          />
          <CardRowSimple label={"Annotated proteins"} value={formatNumberByThousands(genomeStatistics.proteinsCount)} />
          <CardRowSimple
            label={"E.C. protein assignments"}
            value={formatNumberByThousands(genomeStatistics.ecProteinAssignmentsCount)}
          />
          <CardRowSimple
            label={"E.C. coverage"}
            value={(genomeStatistics.ecCoverage ? genomeStatistics.ecCoverage.toFixed(2) : 0) + "%"}
          />
          <CardRowSimple
            label={"Transcripts linked to genes"}
            value={formatNumberByThousands(genomeStatistics.genesLinkedTranscriptsCount)}
          />
          <CardRowSimple
            label={"Proteins linked to genes"}
            value={formatNumberByThousands(genomeStatistics.genesLinkedProteinsCount)}
          />
          <CardRowSimple label={"Genome flags"} value={formatNumberByThousands(genomeStatistics.flagCount)} />
        </>
      ) : (
        <SelMessage id={"noContent.genomicAnnotations"} />
      )}
    </Card>
  );
};

export { GenomicAnnotationCard };
